'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, MessageSquare, Sparkles, Navigation } from 'lucide-react';

interface WelcomeTab {
  title: string;
  heading: string;
  subtitle?: string;
  body: React.ReactNode;
}

const TABS: WelcomeTab[] = [
  {
    title: 'Welcome',
    heading: 'Welcome to Voley!',
    subtitle: 'The communication infrastructure for insurance.',
    body: (
      <>
        <p className="text-base">We&rsquo;re glad to have you here.</p>
        <p className="text-[15px]">
          Built on deep AMS integration, designed for agency-wide collaboration, and evolving with
          the latest messaging innovation, Voley is transforming the insurance ecosystem.
        </p>
      </>
    ),
  },
  {
    title: 'Why Voley',
    heading: 'Why Voley?',
    body: (
      <>
        <p className="text-base font-medium text-gray-900">
          Voley is different because insurance is different.
        </p>
        <p className="text-[15px]">
          We bring all your conversations into one place. Voley connects your systems, providing a
          single source of truth for all communication.
        </p>
        <div>
          <p className="text-[15px] mb-3">
            Voley was created by the hands of agency owners to fill a necessary gap. Designed to:
          </p>
          <div className="flex flex-col gap-2 pl-4">
            <div className="flex items-center gap-2">
              <div className="h-1 w-1 rounded-full bg-accent-blue" />
              <span className="text-[15px] font-semibold text-gray-900">Never miss a lead</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1 w-1 rounded-full bg-accent-blue" />
              <span className="text-[15px] font-semibold text-gray-900">Protect your team</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1 w-1 rounded-full bg-accent-blue" />
              <span className="text-[15px] font-semibold text-gray-900">
                Work smarter, together
              </span>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    title: 'Quick Tour',
    heading: "Let's Show You Around",
    body: (
      <>
        <p className="text-base">
          In the next few steps, we&rsquo;ll walk you through the key areas of your dashboard.
        </p>
        <p className="text-[15px]">
          Just click where we point and you&rsquo;ll be up and running in no time. It only takes a
          couple of minutes.
        </p>
      </>
    ),
  },
];

const TAB_ICONS = [MessageSquare, Sparkles, Navigation];

interface WelcomeModalProps {
  onComplete: () => void;
}

export function WelcomeModal({ onComplete }: WelcomeModalProps) {
  const [activeTab, setActiveTab] = useState(0);

  const isLastTab = activeTab === TABS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastTab) {
      onComplete();
    } else {
      setActiveTab((prev) => prev + 1);
    }
  }, [isLastTab, onComplete]);

  const handleBack = useCallback(() => {
    setActiveTab((prev) => Math.max(0, prev - 1));
  }, []);

  const tab = TABS[activeTab];
  const Icon = TAB_ICONS[activeTab];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to Voley"
        >
          {/* Tab indicators */}
          <div className="flex border-b border-gray-100">
            {TABS.map((t, i) => (
              <button
                key={t.title}
                onClick={() => setActiveTab(i)}
                className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                  i === activeTab ? 'text-accent-blue' : 'text-gray-400'
                }`}
              >
                {t.title}
                {i === activeTab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-accent-blue" />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{tab.heading}</h2>
                {tab.subtitle && (
                  <p className="text-base font-semibold text-gray-600 mt-2">{tab.subtitle}</p>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {tab.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeTab > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous tab"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastTab ? 'Start the tour' : 'Next tab'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastTab ? 'Start Tour' : 'Next'}
                {!isLastTab && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {TABS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeTab ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
